import React, { useState, useCallback } from 'react';
import { Modal, Button, Spinner, Form, InputGroup } from 'react-bootstrap';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useAddCreditMutation } from '../../mutations/credit.mutation';

interface AddCreditModalProps {
  clientId: string;
  clientName: string;
  onSuccess?: () => void;
}

const addCreditSchema = Yup.object().shape({
  amount: Yup.number()
    .required('Amount is required')
    .positive('Amount must be positive')
    .typeError('Amount must be a number'),
  note: Yup.string().nullable(),
  sendNotification: Yup.boolean()
});

function AddCreditModal({ clientId, clientName, onSuccess }: AddCreditModalProps) {
  const [show, setShow] = useState(false);
  const { mutate: addCredit, isLoading } = useAddCreditMutation();

  const formik = useFormik({
    initialValues: {
      amount: '',
      note: '',
      sendNotification: true
    },
    validationSchema: addCreditSchema,
    onSubmit: (values, actions) => {
      addCredit(
        {
          clientId,
          amount: parseFloat(values.amount),
          note: values.note || null,
          sendNotification: values.sendNotification
        },
        {
          onSuccess: () => {
            actions.resetForm();
            setShow(false);
            onSuccess?.();
          }
        }
      );
    }
  });

  const handleCloseModal = useCallback(() => {
    formik.resetForm();
    setShow(false);
  }, [formik]);

  return (
    <>
      <Button variant="success" size="sm" onClick={() => setShow(true)}>
        Add Credit
      </Button>
      <Modal show={show} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>Add Credit for {clientName}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Amount</Form.Label>
              <InputGroup>
                <InputGroup.Text>$</InputGroup.Text>
                <Form.Control
                  type="number"
                  step="0.01"
                  min="0"
                  name="amount"
                  placeholder="0.00"
                  value={formik.values.amount}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  isInvalid={formik.touched.amount && !!formik.errors.amount}
                />
                <Form.Control.Feedback type="invalid">{formik.errors.amount}</Form.Control.Feedback>
              </InputGroup>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Note (optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                name="note"
                placeholder="Enter a reason or description for this credit"
                value={formik.values.note}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Check
                type="checkbox"
                name="sendNotification"
                label="Send notification email to customer"
                checked={formik.values.sendNotification}
                onChange={formik.handleChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button disabled={isLoading} variant="light" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button
            disabled={!formik.isValid || !formik.dirty || isLoading}
            variant="success"
            onClick={() => formik.handleSubmit()}>
            <Spinner size="sm" hidden={!isLoading} animation="border" className="mx-1" />
            {isLoading ? 'Adding...' : 'Add Credit'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default AddCreditModal;
